import { ReactNode, useEffect, useState } from "react";
import { quickTableConfig } from "../../configs";
import { filterConstants } from "../../constants";
import { useQueryString, useSetQueryStringFilter } from "../../hooks";
import {
  ITableHeader,
  TTransactionPeriodSelect,
} from "../../interfaces-and-types";

import { View } from "./View";

export interface IQuickTableProps<ITableData> {
  // identity: used to perform cache & storage
  id: string;

  // title
  tableTitle?: string;
  tableSubtitle?: string;

  // data
  data: ITableData[];
  headers: ITableHeader[];
  isLoading: boolean;
  itemCount: number;

  // display formatting
  displayFormatter?: (item: ITableData) => ReactNode;

  // styling
  tableClassName?: string;

  // render details
  renderDetail?: (item: ITableData) => ReactNode;

  // pagination
  availablePageSizes?: number[];

  // filtering
  toolbarTabs?: ReactNode;

  // extra buttons
  extraButtons?: ReactNode;
  onExport?: () => void;
  onCreateNew?: () => void;
  createNewButtonText?: string;
}

export function QuickTable<ITableData>(props: IQuickTableProps<ITableData>) {
  const { id, data, displayFormatter } = props;

  const formattedTableId = id.replace(" ", "_");

  const defaultPageSize = quickTableConfig.availablePageSizes[0];

  const setQueryStringFilter = useSetQueryStringFilter([
    [filterConstants.searchKey, ""],
    [filterConstants.pageSizeKey, defaultPageSize.toString()],
  ]);

  const query = useQueryString();
  const pg = query.get(filterConstants.currentPageKey);
  const pgSz = query.get(filterConstants.pageSizeKey);
  const search = query.get(filterConstants.searchKey);

  const [firstRowIndex, setFirstRowIndex] = useState(0);
  const [currentPage, setCurrentPage] = useState(pg ? Number(pg) : 1); // TODO: get the default value from url
  const [currentPageSize, setCurrentPageSize] = useState(
    pgSz ? Number(pgSz) : defaultPageSize
  );

  const [transactionPeriod, setTransactionPeriod] =
    useState<TTransactionPeriodSelect>(
      (query.get(
        filterConstants.transactionPeriodKey
      ) as TTransactionPeriodSelect) ||
        filterConstants.transactionPeriods[0].value
    );

  // set currently page on url
  useEffect(() => {
    setQueryStringFilter(
      filterConstants.currentPageKey,
      currentPage.toString()
    );
  }, [currentPage]);

  // set page size on url
  useEffect(() => {
    setQueryStringFilter(
      filterConstants.pageSizeKey,
      currentPageSize.toString()
    );
  }, [currentPageSize]);

  // set transactionPeriod on url
  useEffect(() => {
    setQueryStringFilter(
      filterConstants.transactionPeriodKey,
      transactionPeriod.toString()
    );
  }, [transactionPeriod]);

  // TODO: debounce this to prevent multiple fetches on input
  const filterWithSearch = (searchParams: string) => {
    const trimmedSearchParams = searchParams?.trim()?.toLowerCase();

    return setQueryStringFilter(filterConstants.searchKey, trimmedSearchParams);

  };

  // use firstRowIndex to form query or slice data
  const paginatedData = data.slice(
    firstRowIndex,
    firstRowIndex + currentPageSize
  );

  const displayFormattedData = !displayFormatter
    ? paginatedData
    : paginatedData.map((item) => displayFormatter(item));

  return (
    <View
      {...props}
      id={formattedTableId}
      data={displayFormattedData}
      renderDetail={null}
      currentPageSize={currentPageSize}
      setCurrentPage={setCurrentPage}
      setCurrentPageSize={setCurrentPageSize}
      setFirstRowIndex={setFirstRowIndex}
      transactionPeriod={transactionPeriod}
      onTransactionPeriodChange={setTransactionPeriod}
      onToolbarSearchInputChange={filterWithSearch}
      defaultSearchValue={search}
      toolbarTabs={props.toolbarTabs}
      extraButtons={props.extraButtons}
      onExport={props.onExport}
      onCreateNew={props.onCreateNew}
      createNewButtonText={props.createNewButtonText}
    />
  );
}
